import React, { useCallback, useEffect, useMemo, useState } from "react";
import MainPageDialog from "../../../../../../components/dialog";
import { useForm, useWatch } from "react-hook-form";
import { RadioInputField } from "../../../../../../components/reactHookFormFields/radio";
import { ReactSelectField } from "../../../../../../components/reactHookFormFields/selectField";
import { DatePickerFeild } from "../../../../../../components/reactHookFormFields/datePicker";
import { default_days, default_notification_types, time_slots_24_hours } from "../locations_constants";
import { CheckBox } from "../../../../../../components/reactHookFormFields/checkbox";
import { useAddScheduleMutation, useDeleteScheduleMutation, useGetScheduleQuery, useUpdateScheduleMutation } from "../../../../../../services/schedule_services";
import moment from "moment";
import { MdDelete, MdEdit } from "react-icons/md";
import { toast } from "react-toastify";
import ActionDialog from "../../../../../../components/actionDialog";
import { isDateValid } from "../../../../../../helper";
import { useGroupChatContext } from "../../../../../../context/groupchatContext";

const getTimeSlot = (time) => {
  if(!time) {
    return null;
  }
  return {
    label: time?.split(":")[0] + ":" + time?.split(":")[1],
    value: time?.split(":")[0] + ":" + time?.split(":")[1],
  };
}

function NotificationManagementDialogue({
  setShowNotificationsManagementDialogue = () => {},
  showNotificationsManagementDialogue = {},
  currentLocation = {},
  locationsData = [],
  currentUser = null,
}) {
  const groupChatContext = useGroupChatContext();
  const { authData } = useMemo(() => ({
    authData: groupChatContext.authData,
  }));

  const { control, reset, formState: { errors }, setValue, getValues, handleSubmit, trigger } = useForm({
    defaultValues: {
      hours: "24_hours",
    },
    mode: "onChange"
  });

  // States
  const [deleteScheduleId, setDeleteScheduleId] = useState(null);
  const [updatedScheduleData, setUpdatedScheduleData] = useState(null);
  const [editScheduleId, setEditScheduleId] = useState(null);
  const [updatedStartTimeSlots, setUpdatedStartTimeSlots] = useState({
    monday: time_slots_24_hours,
    tuesday: time_slots_24_hours,
    wednesday: time_slots_24_hours,
    thursday: time_slots_24_hours,
    friday: time_slots_24_hours,
    saturday: time_slots_24_hours,
    sunday: time_slots_24_hours,
  });
  const [updatedEndTimeSlots, setUpdatedEndTimeSlots] = useState({
    monday: time_slots_24_hours,
    tuesday: time_slots_24_hours,
    wednesday: time_slots_24_hours,
    thursday: time_slots_24_hours,
    friday: time_slots_24_hours,
    saturday: time_slots_24_hours,
    sunday: time_slots_24_hours,
  });

  // Queries & Mutations
  const notificationLocationOptions = useMemo(
    () =>
      (locationsData || [])
        .filter((item) => !item?.is_group)
        .map((item) => ({ label: item?.name, value: item?.id, ...item })),
    [locationsData]
  );

  const { data: scheduleData, isLoading: isScheduleLoading, refetch: refetchSchedule } = useGetScheduleQuery({
    user_id: currentUser?.user_id ? currentUser?.user_id : showNotificationsManagementDialogue?.path === "checkin" ? authData?.id : showNotificationsManagementDialogue?.user_data?.user_id,
  });
  const [addScheduleTrigger, { isLoading: isAddScheduleLoading }] = useAddScheduleMutation();
  const [updateScheduleTrigger, { isLoading: isUpdateScheduleLoading }] = useUpdateScheduleMutation();
  const [deleteScheduleTrigger, { isLoading: isDeleteScheduleLoading }] = useDeleteScheduleMutation();

  useEffect(() => {
    setUpdatedScheduleData(scheduleData?.response?.data || []);
  }, [scheduleData?.response?.data]);

  const handleSaveSchedule = useCallback(async (data) => {
    if(!data?.monday_all_day && !data?.tuesday_all_day && !data?.wednesday_all_day && !data?.thursday_all_day && !data?.friday_all_day && !data?.saturday_all_day && !data?.sunday_all_day) {
      toast.error("Please select shift days");
      return;
    }

    const locations = data.location ? data.location : [];
    const locationIds = locations.map((loc) => loc?.id).filter(Boolean).join(",");
    if (!locationIds) {
      toast.error("Please select at least one location");
      return;
    }

    const apiTrigger = editScheduleId ? updateScheduleTrigger : addScheduleTrigger;

    const defaultPayload = {
      location_id: locationIds,
      user_id: showNotificationsManagementDialogue?.path === "checkin" ? authData?.id : showNotificationsManagementDialogue?.user_data?.user_id,
      start_date: moment(data?.from_date).format("YYYY-MM-DD"),
      end_date: moment(data?.to_date).format("YYYY-MM-DD"),
      notify_sms: 0,
      notify_call_phone: 0,
      notify_call_in_app: 0,
    };

    (default_days || []).forEach((day) => {
      defaultPayload[`${day?.key}_all_day`] = data?.hours === "shift_hours" ? null : data?.[`${day?.key}_all_day`] ? 1 : 0;
      defaultPayload[`${day?.key}_start_time`] = data?.hours === "shift_hours" && data?.[`${day?.key}_all_day`] ? data?.[`${day?.key}_start_time`]?.label || null : null;
      defaultPayload[`${day?.key}_end_time`] = data?.hours === "shift_hours" && data?.[`${day?.key}_all_day`] ? data?.[`${day?.key}_end_time`]?.label || null : null;
    });

    if(editScheduleId) {
      defaultPayload.id = editScheduleId;
    }

    data?.type?.forEach((item) => {
        defaultPayload[item?.id] = 1;
    });

    await apiTrigger(defaultPayload)
      .then((res) => {
        const { code, data, msg } = res?.data.response || {};
        if (code === 0 || code === "0") {
          toast.success(msg);
          handleResetForm();
        }
      })
      .catch((err) => {
        console.log("Error in adding schedule", err);
        toast.error("Error in adding schedule");
      })
      .finally(() => {
        refetchSchedule();
        setEditScheduleId(null);
        handleResetForm();
      });
    
  }, [handleSubmit, editScheduleId]);

  const handleSetEditScheduleData = useCallback((data) => {
    let shiftType = "";
    let checkAllDays = true;

    setEditScheduleId(data?.id);

    setUpdatedScheduleData(
      prev => (prev.map((item) => {
        if(item?.id === data?.id) {
          return {
            ...item,
            edit: true,
          }
        }
        return item;
      }))
    );

    if(!data?.monday_all_day && !data?.tuesday_all_day && !data?.wednesday_all_day && !data?.thursday_all_day && !data?.friday_all_day && !data?.saturday_all_day && !data?.sunday_all_day) {
      shiftType = "shift_hours";
    } else {
      shiftType = "24_hours";
    }

    setValue("hours", shiftType);

    setValue("location", [{
      id: data?.location_id,
      label: data?.location_name,
      value: data?.location_id,
    }]);

    const typesData = [];

    if(data?.notify_sms) {
      typesData.push({ label: "Text", id: "notify_sms" });
    }
    if(data?.notify_call_phone) {
      typesData.push({ label: "Phone Call", id: "notify_call_phone" });
    }
    if(data?.notify_call_in_app) {
      typesData.push({ label: "ethizo Call", id: "notify_call_in_app" });
    }

    setValue("type", typesData);

    setValue("from_date", data?.start_date);
    setValue("to_date", data?.end_date);

    (default_days || []).forEach((day) => {
      setValue(`${day?.key}_start_time`, getTimeSlot(data?.[`${day?.key}_start_time`]));
      setValue(`${day?.key}_end_time`, getTimeSlot(data?.[`${day?.key}_end_time`]));

      if((data?.[`${day?.key}_start_time`] && data?.[`${day?.key}_end_time`]) || (shiftType === "24_hours" && data?.[`${day?.key}_all_day`])) {
        setValue(day?.id, true);
      } else {
        setValue(day?.id, false);
        checkAllDays = false;
      }

      setValue("check_all_days", checkAllDays);
    });
  }, []);

  const hoursWatcher = useWatch({ control, name: "hours" });
  const fromDateWatcher = useWatch({ control, name: "from_date" });
  const toDateWatcher = useWatch({ control, name: "to_date" });

  const dayWatchers = {};
  default_days.forEach(day => {
    dayWatchers[day.id] = useWatch({ control, name: day.id });
  });

  useEffect(() => {
    if (hoursWatcher === "shift_hours") {
      default_days.forEach(day => {
        if (dayWatchers[day.id]) {
          trigger([`${day.key}_start_time`, `${day.key}_end_time`]);
        }
      });
    }
  }, [hoursWatcher, ...Object.values(dayWatchers), trigger]);


  const handleDeleteSchedule = useCallback(async (id) => {
    await deleteScheduleTrigger({
      id: id,
    })
      .then((res) => {
        const { code, data, msg } = res?.data?.response || {};
        if(code === 0 || code === "0") {
          toast.success(msg);
        }
      })
      .catch((err) => {
        console.log("Error in deleting schedule", err);
        toast.error("Error in deleting schedule");
      })
      .finally(() => {
        refetchSchedule();
        setDeleteScheduleId(null);
        setEditScheduleId(null);
      });
  }, []);

  const showShiftDays = (data) => {
    try {
      const dayOrder = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
      const selectedDays = [];
      const indices = [];

      let shiftType = "";
    
      if (!data?.monday_all_day && !data?.tuesday_all_day && !data?.wednesday_all_day && !data?.thursday_all_day && !data?.friday_all_day && !data?.saturday_all_day && !data?.sunday_all_day) {
        shiftType = "shift_hours";
      } else {
        shiftType = "24_hours";
      }
  
      dayOrder.forEach((day, index) => {
        const key = shiftType === "24_hours" ? day.toLowerCase() + "_all_day" : day.toLowerCase() + "_start_time";
        if (data?.[key]) {
          selectedDays.push(day);
          indices.push(index);
        }
      });
  
      if (indices.length === 7) {
        return (
          <div className="flex flex-col">
            <p className="text-[10px] text-gray-500">({`${shiftType === "24_hours" ? "24 Hours" : "Shift Hours"}`})</p>
            <p>{"Monday to Sunday"}</p>
          </div>
        );
      } else if (indices.length === 1) {
        return (
          <div className="flex flex-col">
            <p className="text-[10px] text-gray-500">({`${shiftType === "24_hours" ? "24 Hours" : "Shift Hours"}`})</p>
            <p>{`Every ${selectedDays[0]}`}</p>
          </div>
        );
      } else if (indices.length > 1) {
        let isContiguous = true;
        for (let i = 1; i < indices.length; i++) {
          if (indices[i] !== indices[i - 1] + 1) {
            isContiguous = false;
            break;
          }
        }
        if (isContiguous) {
          return (
            <div className="flex flex-col">
              <p className="text-[10px] text-gray-500">({`${shiftType === "24_hours" ? "24 Hours" : "Shift Hours"}`})</p>
              <p>{`${selectedDays[0]} to ${selectedDays[selectedDays.length - 1]}`}</p>
            </div>
          );
        } else {
          return (
            <div className="flex flex-col">
              <p className="text-[10px] text-gray-500">({`${shiftType === "24_hours" ? "24 Hours" : "Shift Hours"}`})</p>
              <p>{`${selectedDays.join(", ")}`}</p>
            </div>
          );
        }
      }
      return "";
    } catch (error) {
      console.error("Error in showShiftDays:", error);
      return "";
    }
  };
  

  const showMessageType = (data) => {
    const messageType = [];
    if(data?.notify_sms) {
      messageType.push("Text");
    }
    if(data?.notify_call_phone) {
      messageType.push("Phone");
    }
    if(data?.notify_call_in_app) {
      messageType.push("ethizo");
    }
    return <div className="flex flex-wrap items-center gap-1">{messageType?.map((item) => <span className="text-xs text-gray-600 font-bold bg-gray-200 px-2 py-1 rounded-md">{item}</span>)}</div>
  }

  const showDateRange = (data) => {
    return `${isDateValid(data?.start_date) ? moment(data?.start_date).format("MM/DD/YYYY") : ""} - ${isDateValid(data?.end_date) ? moment(data?.end_date).format("MM/DD/YYYY") : ""}`
  }

  const handleResetForm = () => {
    setValue("hours", "24_hours");
    setValue('type', []);
    setValue('location', []);
    setValue('from_date', "");
    setValue('to_date', "");

    default_days.forEach((day) => {
      setValue(day?.id, false);
      setValue(`${day?.key}_start_time`, null);
      setValue(`${day?.key}_end_time`, null);
    });
    setValue('check_all_days', false);
  }

  const footerList = useMemo(() => [
    {
      title: "Cancel",
      type: "cancel",
      disabled: isAddScheduleLoading || isUpdateScheduleLoading,
      isDisabled: isAddScheduleLoading || isUpdateScheduleLoading,
      isLoading: isAddScheduleLoading || isUpdateScheduleLoading,
      loading: isAddScheduleLoading || isUpdateScheduleLoading,
      action: () => {
        if(editScheduleId) {
          setEditScheduleId(null);
          handleResetForm();
        } else {
          setShowNotificationsManagementDialogue({
            show: false,
            user_data: null,
          });
          setEditScheduleId(null);
          handleResetForm();
        }
      },
    },
    {
      title: editScheduleId ? "Update" : "Save",
      type: "save",
      disabled: isAddScheduleLoading || isUpdateScheduleLoading,
      isDisabled: isAddScheduleLoading || isUpdateScheduleLoading,
      isLoading: isAddScheduleLoading || isUpdateScheduleLoading,
      loading: isAddScheduleLoading || isUpdateScheduleLoading,
      action: handleSubmit(handleSaveSchedule),
    },
    {
      title: "Reset",
      type: "save",
      disabled: editScheduleId ? true : isAddScheduleLoading || isUpdateScheduleLoading,
      isDisabled: editScheduleId ? true : isAddScheduleLoading || isUpdateScheduleLoading,
      action: handleResetForm,
    },
  ], [isAddScheduleLoading, editScheduleId, handleSubmit, handleSaveSchedule, isUpdateScheduleLoading]);

  const handleUpdateEndTimeSlots = useCallback((slot, day) => {

    if(getValues()[`${day}_end_time`] && slot) {
      const [endHour, endMinute] = getValues()[`${day}_end_time`]?.label?.split(':').map(Number);
      const [startHour, startMinute] = slot.label.split(':').map(Number);
      if(endHour < startHour || (endHour === startHour && endMinute <= startMinute)) {
        setValue(`${day}_end_time`, null);
      }
    }

    if (!slot?.label) {
      setUpdatedEndTimeSlots(prev => ({
        ...prev,
        [day]: time_slots_24_hours
      }));
      return;
    }

    const [startHour, startMinute] = slot.label.split(':').map(Number);
    
    // Filter end time slots to disable those before or equal to the selected start time
    const filteredSlots = time_slots_24_hours.map(endSlot => {
      const [endHour, endMinute] = endSlot.label.split(':').map(Number);
      return {
        ...endSlot,
        disabled: endHour < startHour || (endHour === startHour && endMinute <= startMinute)
      };
    });

    setUpdatedEndTimeSlots(prev => ({
      ...prev,
      [day]: filteredSlots
    }));
  }, [getValues, setValue]);


  return (
    <MainPageDialog
      title={`${currentUser?.name || (showNotificationsManagementDialogue?.path === "checkin" ? authData?.name : showNotificationsManagementDialogue?.user_data?.name || "")} Notification Management ${editScheduleId ? "(Edit)" : ""}`}
      onCancelHandler={() => setShowNotificationsManagementDialogue({
        show: false,
        user_data: null,
      })}
      footerList={footerList}
      footer={true}
      maxHeight={true}
      contentCls="p-3 sm:p-4 md:px-6"
    >
      {(deleteScheduleId ? true : false) && (
        <ActionDialog
          onCancelHandler={() => setDeleteScheduleId(null)}
          setShowModal={() => setDeleteScheduleId(null)}
          onConfirmHandler={() => handleDeleteSchedule(deleteScheduleId)}
          showModal={deleteScheduleId ? true : false}
          title="Are you sure you want to Delete Schedule?"
          headerTitle="Delete Schedule"
          confirmButtonText={isDeleteScheduleLoading ? "Deleting..." : "Yes"}
          cancelButtonText="No"
        />
      )}
      <form onSubmit={handleSubmit(handleSaveSchedule)} className="flex flex-col lg:flex-row w-full text-[#5c5c5c] gap-4 lg:gap-0 min-w-0">
        <div className="w-full lg:w-[28%] min-w-0">
          <div className="flex flex-col items-center gap-2 border-2 border-gray-100 rounded-md h-full bg-slate-50">
            <div className="w-full flex items-center justify-start p-2">
              <RadioInputField
                control={control}
                name="hours"
                data={[
                  { key: "24_hours", label: "24 Hours" },
                  { key: "shift_hours", label: "Shift Hours" },
                ]}
                defaultValue="24_hours"
                errors={errors}
                rules={{
                  required: {
                      value: true,
                      message: "Required",
                  },
                }}
              />
            </div>
            <div className="w-full flex flex-col gap-4 p-2">
              <ReactSelectField 
                control={control} 
                name="location" 
                options={notificationLocationOptions}
                title="Search Location"
                defaultValue={currentLocation ? [{
                  label: currentLocation?.name,
                  value: currentLocation?.id,
                  ...currentLocation,
                }] : []}
                isMulti={true}
                isClearable={false}
                errors={errors}
                rules={{
                  required: {
                    value: true,
                    message: "Location is required",
                  },
                }}
              />
              <ReactSelectField
                control={control}
                name="type"
                options={default_notification_types || []}
                title="Type"
                isMulti={true}
                errors={errors}
                rules={{
                  required: {
                    value: true,
                    message: "Type is required",
                  },
                }}
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div className="min-w-0">
                  <DatePickerFeild 
                    control={control} 
                    name="from_date" 
                    title="From Date" 
                    customStyles="w-full" 
                    rules={{ required: { value: true, message: "Required" } }}
                    dateTime={false}
                    maxDate={toDateWatcher}
                  />
                </div>
                <div className="min-w-0">
                  <DatePickerFeild 
                    control={control} 
                    name="to_date" 
                    title="To Date" 
                    customStyles="w-full" 
                    rules={{ required: { value: true, message: "Required" } }} 
                    dateTime={false}
                    minDate={fromDateWatcher}
                  />
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-4 w-full p-2 border-t-2 border-gray-100">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between w-full gap-2">
                <div className="flex items-center gap-1">
                  <CheckBox control={control} name="check_all_days" onChange={e => default_days.forEach(day => setValue(day.id, e.target.checked))} />
                  <h4 className="text-sm sm:text-[16px] text-gray-500 font-bold">
                    Days
                  </h4>
                </div>
                {hoursWatcher === "24_hours" ? null : (
                  <div className="flex items-center justify-center sm:flex-1 sm:justify-center">
                    <h4 className="text-sm sm:text-[16px] text-gray-500 font-bold">
                      Time (From - To)
                    </h4>
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-3 w-full">
                {(default_days || []).map((day, index) => (
                  <div key={`${day.id}-${index}`} className="flex flex-col sm:flex-row sm:items-center justify-between w-full min-h-[40px] sm:h-[40px] gap-2 py-1 sm:py-0">
                    <div className="flex items-center gap-1 shrink-0">
                      <CheckBox control={control} name={day.id} title={day.label} />
                    </div>
                    {hoursWatcher === "24_hours" ? null : (
                      <div className="flex flex-wrap items-center gap-2 min-w-0">
                        <div className="w-full min-w-[100px] sm:w-[134px] flex-1 sm:flex-initial">
                          <ReactSelectField 
                            control={control} 
                            name={`${day?.key}_start_time`} 
                            options={updatedStartTimeSlots[day?.key] || []}
                            errors={errors}
                            menuPlacement={index === 0 ? "bottom" : "auto"}
                            onChange={(e) => handleUpdateEndTimeSlots(e, day?.key)}
                            rules={{
                              required: {
                                value: (getValues()[`${day?.id}`] && getValues()[`hours`]) === "shift_hours" ? true : false,
                                message: "Time is required",
                              },
                            }}
                          />
                        </div>
                        <div className="w-full min-w-[100px] sm:w-[134px] flex-1 sm:flex-initial">
                          <ReactSelectField 
                            control={control} 
                            name={`${day?.key}_end_time`} 
                            options={updatedEndTimeSlots[day?.key] || []} 
                            errors={errors}
                            menuPlacement={index === 0 ? "bottom" : "auto"}
                            rules={{
                              required: {
                                value: (getValues()[`${day?.id}`] && getValues()[`hours`]) === "shift_hours" ? true : false,
                                message: "Time is required",
                              },
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="w-full lg:w-[72%] lg:ml-1 min-w-0 mt-4 lg:mt-0">
          <div className={`flex flex-col w-full p-2 border-2 border-gray-100 rounded-md h-full min-w-0 ${editScheduleId ? "overflow-hidden" : "overflow-y-auto"}`}>
            <div className="overflow-x-auto overscroll-x-contain overflow-y-auto max-h-[400px] sm:max-h-[500px] lg:max-h-[610px] w-[320px] md:w-auto">
              <div className="flex items-stretch w-full bg-bgGreen text-white shadow-md min-w-[560px]">
                <div className="w-[13%] min-w-[70px] self-stretch border-r border-t border-b border-l border-gray-300 p-2"><h6 className="text-center font-bold text-xs sm:text-sm">Type</h6></div>
                <div className="w-[27%] min-w-[90px] self-stretch border-r border-t border-b border-gray-300 p-2"><h6 className="text-center font-bold text-xs sm:text-sm">Location</h6></div>
                <div className="w-[20%] min-w-[80px] self-stretch border-r border-t border-b border-gray-300 p-2"><h6 className="text-center font-bold text-xs sm:text-sm">Date (From - To)</h6></div>
                <div className="w-[30%] min-w-[100px] self-stretch border-r border-t border-b border-gray-300 p-2"><h6 className="text-center font-bold text-xs sm:text-sm">Shift</h6></div>
                <div className="w-[10%] min-w-[70px] self-stretch border-r border-t border-b border-gray-300 p-2"><h6 className="text-center font-bold text-xs sm:text-sm">Actions</h6></div>
              </div>
              <div className="flex flex-col">
              {(updatedScheduleData || [])?.map((data, index) => (
                <div className={`flex items-stretch w-full min-w-[560px] ${index % 2 === 0 ? "bg-gray-50" : "bg-white"} ${editScheduleId && data.id == editScheduleId ? "opacity-30 bg-gray-400": "" } hover:bg-gray-100`}>
                  <div className="w-[13%] min-w-[70px] self-stretch border-r border-b border-l border-gray-300 p-2 flex items-center justify-start">{showMessageType(data)}</div>
                  <div className="w-[27%] min-w-[90px] self-stretch border-r border-b border-gray-300 p-2  flex items-center justify-center"><h6 className="text-center text-xs truncate" title={data?.location_name}>{data?.location_name || ""}</h6></div>
                  <div className="w-[20%] min-w-[80px] self-stretch border-r border-b border-gray-300 p-2  flex items-center justify-center"><h6 className="text-center text-xs">{showDateRange(data)}</h6></div>
                  <div className="w-[30%] min-w-[100px] self-stretch border-r border-b border-gray-300 p-2  flex items-center justify-center"><h6 className="text-center text-xs">{showShiftDays(data)}</h6></div>
                  <div className="w-[10%] min-w-[70px] flex-[0_0_10%] self-stretch border-r border-b border-gray-300 p-2 flex items-center justify-center">
                    {editScheduleId && data.id === editScheduleId ? null : (
                      <span className="inline-flex items-center justify-center gap-2">
                        <MdEdit
                          size={18}
                          className="text-teal-600 cursor-pointer shrink-0"
                          title="Edit Schedule"
                          aria-label="Edit Schedule"
                          onClick={() => handleSetEditScheduleData(data)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              handleSetEditScheduleData(data);
                            }
                          }}
                        />
                        <MdDelete
                          size={18}
                          color="red"
                          className="cursor-pointer shrink-0"
                          title="Delete Schedule"
                          aria-label="Delete Schedule"
                          onClick={() => setDeleteScheduleId(data?.id)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              setDeleteScheduleId(data?.id);
                            }
                          }}
                        />
                      </span>
                    )}
                  </div>
              </div>
              ))}
              {(scheduleData?.response?.data || []).length === 0 && (
                <div className="flex items-center justify-center w-full h-full p-5">
                  <h6 className="text-center text-gray-500">{isScheduleLoading ? "Loading..." : "No data found"}</h6>
                </div>
              )}
              </div>
            </div>
          </div>
        </div>
      </form>
    </MainPageDialog>
  );
}

export default NotificationManagementDialogue;
